import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ColumnState,
  GridApi,
  GridReadyEvent,
  RowDragEvent,
} from 'ag-grid-community'
import {
  ProgressReportByTeamPageConfig,
  VersionReportStatus,
  VersionReportVersionPageRowEntity,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ReorderRow } from '../../../../../applications/ports/versionReportVersionRepository'
import {
  StoredVersionReportPageDisplayState,
  toStoredColumnState,
} from '../../hooks/pageDisplayState'
import {
  ProgressReportTable,
  ProgressReportTableContent,
} from '../table/ProgressReportTable'
import { DeliverableTaskPopper } from '../dialog/DeliverableTaskPopper'
import {
  ProgressValue,
  WbsItemAggregateCategory,
} from '../../../../../domain/value-object/ProjectPlanSnapshot'
import { useProjectPlanSnapshotRepository } from '../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { aggregatedValueService } from '../../../../../domain/value-object/AggregatedValueVO'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../../lib/functions/workload'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { useWorkloadUnit } from '../../../../hooks/useWorkloadUnit'
import { useColumnSetting } from '../../../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { useTeamReportColumnDef } from '../../hooks/columnDef'
import { useProgressReportDragAndDrop } from '../../hooks/dragAndDrop'
import { useDiscussionActionContext } from '../../context/DiscussionContext'
import { TargetCandidate } from '../../../../../domain/entity/DiscussionEntity'
import ProgressReportGridWbsHeader from '../header/ProgressReportGridConfigEditHeader'
import { ROW_HEIGHT } from '../../../../containers/BulkSheet'
import { ColumnConfigPopper } from '../dialog/ColumnConfigPopper'
import { PendingEdits } from '../../hooks/currentReport'
import { EvaluateResult } from '../../../../../domain/entity/version-report/evaluation'

type ProgressReportByTeamProps = {
  snapshotUuid: string
  pageUuid: string
  editable: boolean
  config: ProgressReportByTeamPageConfig
  rows: VersionReportVersionPageRowEntity[]
  displayState: StoredVersionReportPageDisplayState
  updateDisplayState: (
    updated: Partial<StoredVersionReportPageDisplayState>
  ) => void
  updateRow: (
    rowUuid: string,
    lockVersion: number,
    key: 'status' | 'comment',
    value: string
  ) => Promise<boolean>
  applyReorderedRows: (
    pageUuid: string,
    reorderPayload: ReorderRow[]
  ) => Promise<void>
  callbackWhenStartLoading?: () => void
  callbackWhenFinishLoading?: () => void
  columnStateApplyToken?: number
  pendingEditsRef: MutableRefObject<PendingEdits>
  discardToken: number
  onGridReady?: (e: GridReadyEvent) => void
  onEvaluateRow?: (targetUuid: string) => Promise<EvaluateResult | undefined>
}
export const ProgressReportByTeam = ({
  editable,
  snapshotUuid,
  pageUuid,
  config,
  rows,
  displayState,
  updateDisplayState,
  updateRow,
  applyReorderedRows,
  callbackWhenStartLoading,
  callbackWhenFinishLoading,
  columnStateApplyToken,
  pendingEditsRef,
  discardToken,
  onGridReady,
  onEvaluateRow,
}: ProgressReportByTeamProps) => {
  const [progressValues, setProgressValues] = useState<ProgressValue[]>([])
  const { pivotByTeamUuids } = useProjectPlanSnapshotRepository()
  const { setTargetCandidates } = useDiscussionActionContext()

  const { convertWorkloadFromHourToSelectedUnit } = useWorkloadUnit(
    displayState.workloadUnit || WorkloadUnit.HOUR
  )

  useEffect(() => {
    const candidates: TargetCandidate[] = progressValues.map(v => ({
      uuid: v.uuid,
      name: v.name,
      type: 'TEAM',
    }))
    setTargetCandidates(candidates)
    return () => {
      setTargetCandidates([])
    }
  }, [progressValues, setTargetCandidates])

  const gridApiRef = useRef<GridApi | null>(null)
  const [isGridReady, setIsGridReady] = useState(false)
  const {
    contextRef,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
    onRowDragLeave,
    skipNextFetchRef,
  } = useProgressReportDragAndDrop({
    gridApi: isGridReady ? gridApiRef.current || undefined : undefined,
    progressValues,
    setProgressValues,
    pageUuid,
    rows,
    applyReorderedRows,
  })
  const handleGridReady = useCallback(
    (e: GridReadyEvent) => {
      gridApiRef.current = e.api
      setIsGridReady(true)
      onGridReady?.(e)
    },
    [onGridReady]
  )

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false
      return
    }
    if (!config.rootUuid || config.tableRows.length === 0) {
      setProgressValues([])
      return
    }
    const fn = async () => {
      callbackWhenStartLoading?.()
      try {
        const response = await pivotByTeamUuids(
          snapshotUuid,
          config.rootUuid,
          config.tableRows
        )
        setProgressValues(response)
      } finally {
        callbackWhenFinishLoading?.()
      }
    }
    fn()
  }, [
    config.rootUuid,
    config.tableRows,
    pivotByTeamUuids,
    snapshotUuid,
    callbackWhenStartLoading,
    callbackWhenFinishLoading,
    skipNextFetchRef,
  ])
  const tableContents = useMemo(
    () =>
      progressValues.map(v => {
        const row = rows.find(r => r.targetUuid === v.uuid)
        const aggregateField =
          displayState.aggregateField || AggregateField.WBS_ITEM_WORKLOAD
        const aggregateTarget =
          displayState.aggregateTarget || WbsItemType.DELIVERABLE
        return {
          uuid: v.uuid,
          name: v.name,
          status:
            (row
              ? (pendingEditsRef.current.rows.get(row.uuid)?.status ??
                row.status)
              : undefined) || 'SUNNY',
          comment:
            (row
              ? (pendingEditsRef.current.rows.get(row.uuid)?.comment ??
                row.comment)
              : undefined) || '',
          scheduledProgressRate:
            aggregatedValueService.getFormattedRatePercentage(
              v.planned,
              v.total,
              aggregateTarget,
              aggregateField
            ),
          progressRate: aggregatedValueService.getFormattedRatePercentage(
            v.completed,
            v.total,
            aggregateTarget,
            aggregateField
          ),
          total: aggregatedValueService.getFormattedWithUnitConversion(
            v.total,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          planned: aggregatedValueService.getFormattedWithUnitConversion(
            v.planned,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          completed: aggregatedValueService.getFormattedWithUnitConversion(
            v.completed,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          delay: aggregatedValueService.getFormattedWithUnitConversion(
            v.delay,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          remaining: aggregatedValueService.getFormattedWithUnitConversion(
            v.remaining,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          wbsItemStatus: v.wbsItemStatus,
          team: v.team,
          progressRateDiff: aggregatedValueService.getFormattedRatePercentage(
            v.plannedCompletedDiff,
            v.total,
            aggregateTarget,
            aggregateField
          ),
          scheduledStartDate: dateV2Service
            .fromString(v.scheduledDate?.startDate)
            ?.toDateString(),
          scheduledEndDate: dateV2Service
            .fromString(v.scheduledDate?.endDate)
            ?.toDateString(),
          maxChildScheduledEndDate: v.maxChildScheduledEndDate,
          preceding: aggregatedValueService.getFormattedWithUnitConversion(
            v.preceding,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
          unplanned: aggregatedValueService.getFormattedWithUnitConversion(
            v.unplanned,
            aggregateTarget,
            aggregateField,
            convertWorkloadFromHourToSelectedUnit
          ),
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingEditsRef は ref なので依存不要
    [
      rows,
      progressValues,
      displayState.aggregateField,
      displayState.aggregateTarget,
      convertWorkloadFromHourToSelectedUnit,
      discardToken,
    ]
  )
  const columnSettingDialog = useColumnSetting()
  const ref = useRef<HTMLDivElement>(null)
  const updateColumnStateConfig = useCallback(
    (state: ColumnState[]) => {
      const storedState = state
        .filter(v => v.colId !== 'drag')
        .map(v => toStoredColumnState(v))
      updateDisplayState({ columnState: storedState })
    },
    [updateDisplayState]
  )

  const updateTableContent = useCallback(
    (
      v: { uuid: string; status: VersionReportStatus; comment: string },
      _index: number
    ) => {
      const row = rows.find(r => r.targetUuid === v.uuid)
      if (!row) return
      const pendingStatus =
        pendingEditsRef.current.rows.get(row.uuid)?.status ?? row.status
      const pendingComment =
        pendingEditsRef.current.rows.get(row.uuid)?.comment ?? row.comment
      if (v.status !== pendingStatus) {
        void updateRow(row.uuid, row.lockVersion, 'status', v.status)
      } else if (v.comment !== pendingComment) {
        void updateRow(row.uuid, row.lockVersion, 'comment', v.comment)
      }
    },
    [rows, updateRow, pendingEditsRef]
  )

  const [popperState, setPopperState] = useState<{
    anchorEl: HTMLElement
    clickPosition: { x: number; y: number }
    teamUuid: string
    aggregateType: WbsItemAggregateCategory
  } | null>(null)
  const closePopper = useCallback(() => setPopperState(null), [])
  const handleNumericCellClick = useCallback(
    (
      data: ProgressReportTableContent,
      anchorEl: HTMLElement,
      clickPosition: { x: number; y: number },
      aggregateType: WbsItemAggregateCategory
    ) => {
      if (!config.rootUuid) return
      setPopperState({
        anchorEl,
        clickPosition,
        teamUuid: data.uuid,
        aggregateType,
      })
    },
    [config.rootUuid]
  )
  const handleRowDragEnter = useCallback(
    (event: RowDragEvent) => {
      setPopperState(null)
      onRowDragEnter(event)
    },
    [onRowDragEnter]
  )

  useEffect(() => {
    if (!editable) setPopperState(null)
    gridApiRef.current?.refreshCells({ force: true })
  }, [editable])

  const columns = useTeamReportColumnDef({
    editable,
    updateTableContent,
    onNumericCellClick: handleNumericCellClick,
  })

  useEffect(() => {
    gridApiRef.current?.resetRowHeights()
  }, [displayState.rowHeight])

  return (
    <>
      <ProgressReportGridWbsHeader
        editable={editable}
        aggregateTarget={
          displayState.aggregateTarget || WbsItemType.DELIVERABLE
        }
        onChangeAggregateTarget={v =>
          updateDisplayState({ aggregateTarget: v })
        }
        onChangeAggregateField={v => updateDisplayState({ aggregateField: v })}
        aggregateField={
          displayState.aggregateField || AggregateField.WBS_ITEM_WORKLOAD
        }
        onChangeWorkloadUnit={v => updateDisplayState({ workloadUnit: v })}
        workloadUnit={displayState.workloadUnit || WorkloadUnit.HOUR}
        rowHeight={displayState.rowHeight || ROW_HEIGHT.MEDIUM}
        onChangeHeight={v => updateDisplayState({ rowHeight: v })}
        onClickColumnSettingButton={columnSettingDialog.toggle}
        columnSettingOpen={columnSettingDialog.isOpen}
      />
      <ProgressReportTable
        ref={ref}
        columns={columns}
        editable={editable}
        contents={tableContents}
        context={contextRef.current}
        onGridReady={handleGridReady}
        onRowDragEnter={handleRowDragEnter}
        onRowDragMove={onRowDragMove}
        onRowDragLeave={onRowDragLeave}
        onRowDragEnd={onRowDragEnd}
        onEvaluateRow={onEvaluateRow}
        rowHeight={displayState.rowHeight || ROW_HEIGHT.MEDIUM}
        updateColumnStateConfig={updateColumnStateConfig}
        columnState={displayState.columnState}
        columnStateApplyToken={columnStateApplyToken}
      />
      <DeliverableTaskPopper
        anchorEl={popperState?.anchorEl ?? null}
        clickPosition={popperState?.clickPosition}
        rootUuid={config.rootUuid}
        teamUuid={popperState?.teamUuid}
        aggregateType={popperState?.aggregateType}
        aggregateTarget={displayState.aggregateTarget || WbsItemType.TASK}
        snapshotUuid={snapshotUuid}
        onClose={closePopper}
      />
      {editable && columnSettingDialog.isOpen && (
        <ColumnConfigPopper
          anchorEl={columnSettingDialog.anchorEl as Element | undefined}
          onClose={columnSettingDialog.close}
          gridApi={gridApiRef.current!}
          updateConfig={updateColumnStateConfig}
          height={ref.current?.offsetHeight}
        />
      )}
    </>
  )
}
